import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Box } from "@mui/material";
import { TextInputComponent } from "../../../../common/components";
import {
  addressData,
  extractAllOptions,
  filterOptionsByValue,
} from "../../../../utils/extractStateCityZipCode";
import {
  cityOptions,
  stateOptions,
  codeOptions,
} from "../../../../utils/extractStateCityZipCode";
import { debounce, set } from "lodash";

type Props = {
  namePrefix?: string;
  postCode?: string;
  disabled?: boolean;
  values: any;
  setFieldValue?:
    | ((field: string, value: any, shouldValidate?: boolean) => void)
    | undefined;
};

const countryOptions = [{ label: "United States", value: "United States" }];

const AddressFilter: React.FC<Props> = ({
  namePrefix,
  postCode = "postcode",
  disabled = false,
  values,
  setFieldValue,
}) => {
  const getField = (field: string) =>
    namePrefix ? `${namePrefix}.${field}` : `${field}`;
  const currentValues = namePrefix ? values?.[namePrefix] : values || {};
  const [zipCodePage, setZipCodePage] = useState(1);
  const PAGE_SIZE = 100;

  const [filteredOptions, setFilteredOptions] = useState<any>({
    cityOptions: cityOptions || [],
    stateOptions: stateOptions || [],
    // postCodeOptions: codeOptions || [],
    filtered: [],
  });

  const [inputValue, setInputValue] = useState<any>({});

  const handleInputValue = useMemo(
    () =>
      debounce((value: string, field: string) => {
        setInputValue({ value, field });
      }, 500),
    []
  );

  const handleCityChange = (option: any) => {
    if (!option) {
      // User cleared the input
      setFieldValue?.(getField("city"), "");
      return;
    }
    setFieldValue?.(getField("city"), option?.value);

    const result = filterOptionsByValue(
      filteredOptions?.filtered,
      "City",
      option?.value
    );
    handleToCheckValueExitsInOptions(result, "city");
    setFilteredOptions({ ...result });
  };

  const findValueorNot = (options: any, value: string) => {
    return options?.find((opt: any) => opt.value === value) || null;
  };

  const handleToCheckValueExitsInOptions = (options: any, key: string) => {
    if (
      currentValues?.state &&
      key !== "state" &&
      !findValueorNot(options?.stateOptions, currentValues?.state)
    ) {
      setFieldValue?.(getField("state"), "");
    }
    if (
      currentValues?.city &&
      key !== "city" &&
      !findValueorNot(options?.cityOptions, currentValues?.city)
    ) {
      setFieldValue?.(getField("city"), "");
    }
    // if (
    //   currentValues?.[postCode] &&
    //   key !== "code" &&
    //   !findValueorNot(options?.postCodeOptions, currentValues?.[postCode])
    // ) {
    //   setFieldValue?.(getField(postCode), "");
    // }
  };

  const handlePincodeChange = (option: any) => {
    if (!option) {
      // User cleared the input
      setFieldValue?.(getField(postCode), "");
      setFilteredOptions((prev: any) => ({
        ...prev,
        postCodeOptions: codeOptions?.slice(0, 1 * PAGE_SIZE),
      }));
      setZipCodePage(1);
      return;
    }
    setFieldValue?.(getField(postCode), option?.value);
    const result = filterOptionsByValue(
      filteredOptions?.filtered,
      "Code",
      option?.value
    );
    handleToCheckValueExitsInOptions(result, "code");
    setFilteredOptions({ ...result });
  };

  const displayedZipCodes = useMemo(() => {
    setFilteredOptions((prev: any) => ({
      ...prev,
      postCodeOptions: codeOptions?.slice(0, zipCodePage * PAGE_SIZE),
    }));
    return codeOptions?.slice(0, zipCodePage * PAGE_SIZE);
  }, [zipCodePage]);

  useEffect(() => {
    if (inputValue?.value?.length >= 2) {
      const lower = inputValue?.value?.toLowerCase();
      const key = inputValue?.field;
      const data = key === "City" ? filteredOptions?.filtered : addressData;
      if (data?.length) {
        const filtered = data?.filter((c: any) =>
          c?.[key]?.toLowerCase().includes(lower)
        );
        const optionsData = extractAllOptions(filtered);
        if (key === "City") {
          setFilteredOptions((prev: any) => ({
            ...prev,
            cityOptions: optionsData?.cityOptions?.slice(0, 250),
            filtered,
          }));
        } else {
          const options: any = {};
          if (key === "State") {
            options.stateOptions = optionsData?.stateOptions?.slice(0, 100);
          }
          // if (key === "Code") {
          //   options.postCodeOptions = optionsData?.postCodeOptions?.slice(
          //     0,
          //     250
          //   );
          // }
          setFilteredOptions((prev: any) => ({
            ...prev,
            ...options,
            filtered,
          }));
        }
      }
    } else {
      setFilteredOptions({
        cityOptions: cityOptions || [],
        stateOptions: stateOptions || [],
        // postCodeOptions: displayedZipCodes || [],
        filtered: [],
      });
    }
  }, [inputValue]);

  useEffect(() => {
    let result;
    // if (currentValues?.[postCode]) {
    //   result = filterOptionsByValue(
    //     addressData,
    //     "Code",
    //     currentValues[postCode]
    //   );
    // }
    if (currentValues?.city) {
      result = filterOptionsByValue(
        addressData,
        "City",
        currentValues.city,
        "State",
        currentValues.state
      );
    } else if (currentValues?.state) {
      result = filterOptionsByValue(addressData, "State", currentValues.state);
    }
    if (result) {
      const noFilteredData = !result?.filtered?.length && !inputValue?.value;

      setFilteredOptions(
        noFilteredData
          ? {
              cityOptions: cityOptions || [],
              stateOptions: stateOptions || [],
              // postCodeOptions: codeOptions || [],
              filtered: [],
            }
          : { ...result }
      );
    }
  }, [currentValues?.city, currentValues?.state]);

  return (
    <>
      <Box
        className="address-details-content address-details-wrapper tenant-address-filter-wrapper"
        gap={3}
      >
        <Box className="address-content-header">
          <ul className="equal-width-list">
            <li className="list-item">
              <span className="item-value">
                <Select
                  inputId="Country"
                  classNamePrefix="react-select"
                  options={countryOptions}
                  placeholder="Select Country"
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  value={
                    countryOptions.find(
                      (opt: any) => opt.value === currentValues?.country
                    ) || null
                  }
                  onChange={(option: any) => {
                    if (!option) {
                      // User cleared the input
                      setFilteredOptions((prev: any) => ({
                        codeOptions: countryOptions,
                        cityOptions: [],
                        // postCodeOptions: codeOptions?.slice(0, 1 * PAGE_SIZE),
                        filtered: [],
                        stateOptions: stateOptions || [], // reset full list
                      }));
                      setFieldValue?.(getField("country"), "");
                      setFieldValue?.(getField("state"), "");
                      setFieldValue?.(getField("city"), "");
                      setZipCodePage(1);
                      return;
                    }
                    setFieldValue?.(getField("country"), option?.value);
                  }}
                  isClearable={true}
                  isSearchable={true}
                  isDisabled={disabled}
                  menuPlacement="auto"
                />
              </span>
            </li>
            <li className="list-item">
              <span className="item-value">
                <Select
                  inputId="State"
                  options={filteredOptions.stateOptions?.sort(
                    (a: any, b: any) => a?.value.localeCompare(b?.value)
                  )}
                  placeholder="Select State"
                  classNamePrefix="react-select"
                  value={
                    filteredOptions?.stateOptions?.find(
                      (opt: any) => opt.value === currentValues?.state
                    ) || null
                  }
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  onChange={(option: any) => {
                    if (!option) {
                      // User cleared the input
                      setFilteredOptions((prev: any) => ({
                        cityOptions: [],
                        // postCodeOptions: codeOptions?.slice(0, 1 * PAGE_SIZE),
                        filtered: [],
                        stateOptions: stateOptions, // reset full list
                      }));
                      setFieldValue?.(getField("state"), "");
                      setFieldValue?.(getField("city"), "");
                      setZipCodePage(1);
                      return;
                    }
                    const result = filterOptionsByValue(
                      filteredOptions?.filtered,
                      "State",
                      option?.value
                    );
                    handleToCheckValueExitsInOptions(result, "state");
                    setFilteredOptions({ ...result });
                    setFieldValue?.(getField("state"), option?.value);
                  }}
                  isClearable={true}
                  isSearchable={true}
                  isDisabled={!currentValues?.country || disabled}
                  menuPlacement="top"
                />
              </span>
            </li>
            <li className="list-item">
              <span className="item-value">
                <Select
                  inputId="City"
                  classNamePrefix="react-select"
                  options={filteredOptions.cityOptions?.sort((a: any, b: any) =>
                    a?.value.localeCompare(b?.value)
                  )}
                  placeholder="Select City"
                  isDisabled={!currentValues?.state || disabled}
                  value={
                    filteredOptions.cityOptions.find(
                      (opt: any) => opt.value === currentValues?.city
                    ) || null
                  }
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  onChange={handleCityChange}
                  isClearable={true}
                  isSearchable={true}
                  menuPlacement="top"
                />
              </span>
            </li>
            {/* <li className="list-item">
              <span className="item-value">
                <Select
                  inputId="Postcode"
                  classNamePrefix="react-select"
                  options={filteredOptions.postCodeOptions}
                  placeholder="Select zip code"
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  value={
                    filteredOptions.postCodeOptions.find(
                      (opt: any) => opt.value === currentValues?.[postCode]
                    ) || null
                  }
                  // onInputChange={(val) => handleInputValue(val, "Code")}
                  onInputChange={(val, actionMeta) => {
                    if (actionMeta.action === "input-change") {
                      handleInputValue(val, "Code");
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    //if there is no state and city selected, load more zip codes
                    //if there is state or city selected then based on that value we will get zip codes
                    if (
                      !currentValues?.state &&
                      !currentValues?.city &&
                      inputValue.field !== "Code" &&
                      zipCodePage * PAGE_SIZE < codeOptions?.length
                    ) {
                      setZipCodePage((prev) => prev + 1);
                    }
                  }}
                  onChange={handlePincodeChange}
                  isClearable={true}
                  isSearchable={true}
                  isDisabled={disabled}
                />
              </span>
            </li> */}
            <li className="list-item postcode-input-field">
              <div className="tenant-filter-options-text-wrapper">
                <div className="filter-options-tenant-filters">
                  <TextInputComponent
                    type="text"
                    fieldName="postcode"
                    placeholder="Enter Zipcode"
                    value={values?.postcode}
                    onChange={(e: any) => {
                      let zip = e.target.value;
                      // Optional: filter out non-digits without blocking input
                      zip = zip.replace(/\D/g, '').slice(0, 5); // ✅ removes non-digits, trims to 5
                      setFieldValue?.("postcode", zip);
                      
                    }}
                  />
                </div>
              </div>
            </li>
          </ul>
        </Box>
      </Box>
    </>
  );
};

export default AddressFilter;
